import {
  CALENDAR_EMPTY_CARD_WIDTH,
  DEFAULT_CALENDAR_COL_SPACING,
  DEFAULT_CALENDAR_CARD_SPACING,
  DEFAULT_CALENDAR_CARD_WIDTH,
  SINGLE_CALENDAR_CARD_COLS_NUM,
} from '../constants';

import type { ICalendarColCfgMapping, IUtilsCommonParams } from '../types';

/**
 * 判断日历数据是否为空
 * @param dataList 日历数据
 * @return 日历数据是否为空
 */
export const getIsDataListEmpty = (dataList?: CommonCalendarDataTypes): boolean => {
  if (!Array.isArray(dataList) || !dataList.length) return true;
  return dataList.every((dataItem) => {
    const { date, list } = dataItem || {};
    return !date || !Array.isArray(list) || !list.length;
  });
};

/**
 * 计算每一列的每一行展示的日历卡片的平均数量（每列的卡片宽度都按照有数的日历卡片宽度来计算的，但是并不是每一列的日历卡片宽度都是有数据的宽度，所以是平均数量）
 * 列数大于等于SINGLE_CALENDAR_CARD_COLS_NUM时，每一列的每一行仅展示一张日历卡片
 * 否则每一列的每一行展示日历卡片的数量根据用户屏幕宽度自动计算
 * 当列数大于SINGLE_CALENDAR_CARD_COLS_NUM时，每一列的每一行展示日历卡片的数量公式推导如下：
 *    假设每一列的每一行展示的日历卡片数量为x，进行以下推导
 *    colWidth = x * cardWidthTmp + cardSpacing * (x - 1)
 *    colWidth = x * cardWidthTmp + (cardSpacing * x - cardSpacing)
 *    colWidth = x * cardWidthTmp + cardSpacing * x - cardSpacing
 *    colWidth + cardSpacing = x * cardWidthTmp + cardSpacing * x
 *    colWidth + cardSpacing = x * (cardWidthTmp + cardSpacing)
 *    x = (colWidth + cardSpacing) / (cardWidthTmp + cardSpacing)
 * @param params 参数对象
 * @param params.cardListEl 日历卡片外层容器（支持水平滚动那个容器）
 * @param params.calendarDataList 日历数据
 * @param params.colSpacing 列间距
 * @param params.cardSpacing 日历卡片间距
 * @param params.cardWidth 日历卡片宽度
 * @returns 每一列的每一行展示的日历卡片的数量
 */
const getAvgCardsNumPerColRow = (params?: IUtilsCommonParams): number => {
  const { cardListEl, calendarDataList, colSpacing, cardSpacing, cardWidth } = params || {};
  // 列数（一个时间节点对应一列）
  const colsNum = calendarDataList?.length || 0;
  // 无日历数据时，一行展示的日历卡片数量为0
  if (colsNum === 0) return 0;
  // 列数大于等于SINGLE_CALENDAR_CARD_COLS_NUM时，每一列的每一行仅展示一张日历卡片
  if (colsNum >= SINGLE_CALENDAR_CARD_COLS_NUM) return 1;
  // 日历卡片外层容器宽度（支持水平滚动那个容器）
  const cardListWidth = cardListEl?.offsetWidth || 0;
  // 列间距
  const colSpacingTmp = colSpacing || DEFAULT_CALENDAR_COL_SPACING;
  // 日历卡片间距
  const cardSpacingTmp = cardSpacing || DEFAULT_CALENDAR_CARD_SPACING;
  // 日历卡片宽度
  const cardWidthTmp = cardWidth || DEFAULT_CALENDAR_CARD_WIDTH;
  /**
   * 列的总宽度 = 日历卡片外层容器宽度 - (列间距 * (列数 - 1))
   */
  const colsWidth = Math.max(cardListWidth - colSpacingTmp * (colsNum - 1), 0);
  // 列的宽度 = 列的总宽度 / 列数
  const colWidth = colsWidth / colsNum;
  // 每一列的每一行展示日历卡片的数量（这一步公式推导过程，在方法注释中有解释）(最小值为1)
  return Math.max(Math.floor((colWidth + cardSpacingTmp) / (cardWidthTmp + cardSpacingTmp)), 1);
};

/**
 * 获取指定列每一行展示的日历卡片数量
 * 指定列的每一行展示的日历卡片数量
 * 1. 如果指定列没有数据，则该列每一行展示的日历卡片数量为1
 * 2. 否则取listLen与cardsNumPerRow的最小值（最小兜底数量为1）
 * @param listLen 指定列下的日历数据长度
 * @param avgCardsNumPerRow 每一列的每一行展示日历卡片数量的平均值
 * @return 指定列每一行展示的日历卡片数量
 */
const getCalendarCardsNumPerRow = (listLen?: number, avgCardsNumPerRow?: number): number => {
  const listLenTmp = listLen || 0;
  const avgCardsNumPerRowTmp = avgCardsNumPerRow || 0;
  return !listLenTmp ? 1 : Math.max(Math.min(listLenTmp, avgCardsNumPerRowTmp), 1);
};

/**
 * 获取指定列的日历卡片宽度
 * @param listLen 指定列下的日历数据长度
 * @param cardWidth 日历卡片宽度（无关哪一列，而是当前尺寸下的有数据的日历卡片宽度）
 * @return 指定列的日历卡片宽度
 */
const getCalendarCardWidth = (listLen?: number, cardWidth?: number): number => {
  const cardWidthTmp = cardWidth || DEFAULT_CALENDAR_CARD_WIDTH;
  return listLen && listLen > 0 ? cardWidthTmp : CALENDAR_EMPTY_CARD_WIDTH;
};

/**
 * 获取指定列的宽度
 * @param cardsNumPerRow 指定列每一行展示的日历卡片数量
 * @param cardSpacing 日历卡片间距
 * @param cardWidth 日历卡片宽度
 * @return 指定列的宽度
 */
const getCalendarColWidth = (cardsNumPerRow?: number, cardSpacing?: number, cardWidth?: number): number => {
  // 当前列每一行展示的日历卡片数量
  const cardsNumPerRowTmp = cardsNumPerRow || 0;
  // 日历卡片间距
  const cardSpacingTmp = cardSpacing || DEFAULT_CALENDAR_CARD_SPACING;
  // 当前列的卡片宽度
  const cardWidthTmp = cardWidth || DEFAULT_CALENDAR_CARD_WIDTH;
  // 当前列宽度
  return cardWidthTmp * cardsNumPerRowTmp + cardSpacingTmp * Math.max(cardsNumPerRowTmp - 1, 0);
};

/**
 * 获取日历卡片列与列配置信息的映射（键为列，即列对应的日期，值为该列的配置信息）
 * @param params 参数对象 具体字段参见IUtilsCommonParams类型定义
 * @return 日历卡片列与列配置信息的映射（键为列，即列对应的日期，值为该列的配置信息）
 */
export const getCalendarColCfgMapping = (params?: IUtilsCommonParams): ICalendarColCfgMapping => {
  const { cardSpacing, cardWidth, calendarDataList } = params || {};
  if (!Array.isArray(calendarDataList) || !calendarDataList.length) return {};
  // 日历卡片列与列配置信息的映射（键为列，即列对应的日期，值为该列的配置信息）
  const mapping: ICalendarColCfgMapping = {};
  // 每一列的每一行展示的日历卡片数量的平均值
  const avgCardsNumPerRow = getAvgCardsNumPerColRow(params);
  for (const calendarDataItem of calendarDataList) {
    const { date, list } = calendarDataItem || {};
    if (!date) continue;
    // 当前列下的日历数据长度
    const listLen = list?.length || 0;
    // 当前列每一行展示的日历卡片数量
    const cardsNumPerRowTmp = getCalendarCardsNumPerRow(listLen, avgCardsNumPerRow);
    // 当前列的日历卡片宽度
    const cardWidthTmp = getCalendarCardWidth(listLen, cardWidth);
    // 当前列宽度
    const widthTmp = getCalendarColWidth(cardsNumPerRowTmp, cardSpacing, cardWidthTmp);
    mapping[date] = {
      width: widthTmp,
      cardsNumPerRow: cardsNumPerRowTmp,
      cardWidth: cardWidthTmp,
    };
  }
  return mapping;
};
